import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  /** Identifies the latest variant update this gallery is handling, so an older one can never replace a newer one. */
  #latestUpdate = 0;

  /**
   * Resolves once the first photos of `newGallery` have downloaded (or after a short cap), so the gallery can
   * be swapped in one step instead of photos popping in one at a time. Never rejects. Uses `complete` / the
   * load event rather than decode(), which never settles while the tab is not painting.
   *
   * @param {Element} newGallery - The server-rendered gallery about to replace this one.
   */
  #photosReady = async (newGallery) => {
    try {
      const wanted = new Map();
      for (const img of newGallery.querySelectorAll('img.product-media__image')) {
        if (!(img instanceof HTMLImageElement) || img.getAttribute('loading') === 'lazy') continue;
        const key = `${img.getAttribute('srcset')}|${img.getAttribute('sizes')}`;
        if (!wanted.has(key)) wanted.set(key, img);
        if (wanted.size >= 4) break;
      }

      const loads = [...wanted.values()].map((source) => {
        const preload = new Image();
        preload.sizes = source.getAttribute('sizes') ?? '';
        preload.srcset = source.getAttribute('srcset') ?? '';
        preload.src = source.getAttribute('src') ?? '';
        if (preload.complete && preload.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          preload.addEventListener('load', resolve, { once: true });
          preload.addEventListener('error', resolve, { once: true });
        });
      });

      await Promise.race([Promise.all(loads), new Promise((resolve) => setTimeout(resolve, 1200))]);
    } catch (error) {
      /* swap anyway */
    }
  };

  #handleVariantUpdate = async (event) => {
    // Combined-listing pack switches morph the entire <main> first
    // (variant-picker.js:329), which moves the source HTML's nodes into the
    // live DOM. The source media-gallery is now an empty husk — replacing
    // the live one with it would crash zoom-dialog with
    // MissingRefError("media") and break the Custom-Fit tab. The morph
    // already updated us, so skip the replacement.
    if (event.detail.data.newProduct) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const update = ++this.#latestUpdate;
    await this.#photosReady(newMediaGallery);
    // Superseded by a newer colour click, or this gallery was already replaced / removed while waiting.
    if (update !== this.#latestUpdate || !this.isConnected) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
